import React, { useState, useMemo, useEffect } from 'react'
import { UserService } from '@duck/api-definitions'
import { UsersTableFilters } from './UsersTableFilters'
import { UsersTable } from './UsersTable'
import { UserDialog } from './UserDialog'
import { baseColumns, type Column } from './utils'
import { apis } from '../../modules/apiClients'
import { useTar } from '@/modules'

const STORAGE_KEY = 'admin-user-filters'

interface FilterState {
  sortField: keyof UserService.User
  sortDirection: 'asc' | 'desc'
  filterText: string
  roleFilter: string[]
  statusFilter: string[]
  visibleColumnKeys: string[]
}

// Utility functions for localStorage
const loadFiltersFromStorage = (): FilterState | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

const saveFiltersToStorage = (filters: FilterState): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
  } catch {
    // Ignore localStorage errors
  }
}

const reconstructColumns = (visibleKeys?: string[]): Column[] => {
  const keys = visibleKeys || baseColumns.filter(col => col.visible).map(col => col.key)
  return baseColumns.map(col => ({
    ...col,
    visible: keys.includes(col.key)
  }))
}

export const UsersManagement: React.FC = () => {
  const { t } = useTar()
  // Initialize state from localStorage or defaults
  const storedFilters = loadFiltersFromStorage()

  const [sortField, setSortField] = useState<keyof UserService.User>(
    storedFilters?.sortField ?? 'createdAt'
  )
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    storedFilters?.sortDirection ?? 'desc'
  )
  const [filterText, setFilterText] = useState(storedFilters?.filterText ?? '')
  const [roleFilter, setRoleFilter] = useState<string[]>(
    storedFilters?.roleFilter ?? []
  )
  const [statusFilter, setStatusFilter] = useState<string[]>(
    storedFilters?.statusFilter ?? []
  )
  const [columnDropdownOpen, setColumnDropdownOpen] = useState(false)
  const [columns, setColumns] = useState<Column[]>(
    () => reconstructColumns(storedFilters?.visibleColumnKeys)
  )

  // API state
  const [users, setUsers] = useState<UserService.User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [allAccessGroups, setAllAccessGroups] = useState<string[]>([])

  // Dialog state
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserService.User | null>(null)

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apis.userService.rootGet()
      setUsers(response.data)
      // Collect all unique access groups from all users
      const accessGroupsSet = new Set<string>()
      response.data.forEach(user => {
        if (user.accessGroups && Array.isArray(user.accessGroups)) {
          user.accessGroups.forEach(group => accessGroupsSet.add(group))
        }
      })
      setAllAccessGroups(Array.from(accessGroupsSet).sort())
    } catch {
      setError('Failed to load users. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  // Save to localStorage whenever filter state changes
  useEffect(() => {
    const visibleColumnKeys = columns.filter(col => col.visible).map(col => col.key)
    const filterState: FilterState = {
      sortField,
      sortDirection,
      filterText,
      roleFilter,
      statusFilter,
      visibleColumnKeys,
    }
    saveFiltersToStorage(filterState)
  }, [sortField, sortDirection, filterText, roleFilter, statusFilter, columns])

  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users

    // Text filter
    if (filterText) {
      const searchTerm = filterText.toLowerCase()
      filtered = filtered.filter(user =>
        (user.email ?? '').toLowerCase().includes(searchTerm) ||
        (user.firstName ?? '').toLowerCase().includes(searchTerm) ||
        (user.lastName ?? '').toLowerCase().includes(searchTerm) ||
        (user.notes ?? '').toLowerCase().includes(searchTerm)
      )
    }

    // Role filter (multi)
    if (roleFilter.length > 0) {
      const roleSet = new Set(roleFilter)
      filtered = filtered.filter(user => roleSet.has(user.role as string))
    }

    // Status filter (multi)
    if (statusFilter.length > 0) {
      filtered = filtered.filter(user => {
        return statusFilter.some(statusKey => {
          switch (statusKey) {
            case 'active-verified':
              return user.isActive && user.isVerified
            case 'active-unverified':
              return user.isActive && !user.isVerified
            case 'inactive':
              return !user.isActive
            default:
              return false
          }
        })
      })
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      const aValue = a[sortField]
      const bValue = b[sortField]

      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      let comparison = 0
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue)
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue
      } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        comparison = aValue === bValue ? 0 : aValue ? 1 : -1
      } else {
        comparison = String(aValue).localeCompare(String(bValue))
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return sorted
  }, [users, filterText, roleFilter, statusFilter, sortField, sortDirection])

  const handleSort = (field: keyof UserService.User) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const toggleColumnVisibility = (columnKey: string) => {
    setColumns(prev => prev.map(col =>
      col.key === columnKey
        ? { ...col, visible: !col.visible }
        : col
    ))
  }


  const handleUserClick = (user: UserService.User) => {
    setSelectedUser(user)
    setIsUserDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsUserDialogOpen(false)
    setSelectedUser(null)
  }

  const handleUserSaved = () => {
    fetchUsers()
  }

  const visibleColumns = columns.filter(col => col.visible)

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold leading-7 text-foreground sm:truncate sm:text-3xl sm:tracking-tight">
            {t('users.userManagement')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('users.userManagementDescription')}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => fetchUsers()}
            className="px-4 py-2 bg-secondary text-foreground rounded-md text-sm font-medium hover:bg-secondary/80 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {/* Filters and Controls */}
      <UsersTableFilters
        filterText={filterText}
        setFilterText={setFilterText}
        roleFilter={roleFilter}
        setRoleFilter={setRoleFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        columns={columns}
        columnDropdownOpen={columnDropdownOpen}
        setColumnDropdownOpen={setColumnDropdownOpen}
        toggleColumnVisibility={toggleColumnVisibility}
      />

      {/* Error State */}
      {error && (
        <div className="rounded-md bg-error/10 border border-error/20 p-4">
          <div className="text-error text-sm">{error}</div>
        </div>
      )}

      {/* Users Table */}
      {!error && (
        <UsersTable
          users={filteredAndSortedUsers}
          totalUsers={users.length}
          visibleColumns={visibleColumns}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          onUserClick={handleUserClick}
          loadingMode={loading}
        />
      )}

      {/* User Dialog */}
      <UserDialog
        isOpen={isUserDialogOpen}
        onClose={handleCloseDialog}
        user={selectedUser}
        onUserSaved={handleUserSaved}
        allAccessGroups={allAccessGroups}
      />
    </div>
  )
}
